import { TopicGroup } from '../algebra1/lessons';

export const pythagorean: TopicGroup = {
  id: 'pythagorean',
  title: 'Pythagorean Theorem',
  icon: 'ri-triangle-line',
  lessons: [
    {
      id: 'pythagorean-basics',
      title: 'Pythagorean Theorem Basics',
      description: 'Master the fundamental theorem for right triangles',
      textbookExplanation: `
# The Pythagorean Theorem

The **Pythagorean Theorem** is one of the most important theorems in mathematics. It relates the three sides of a right triangle.

## The Theorem:
In a right triangle, the square of the hypotenuse equals the sum of the squares of the two legs.

**Formula:** a² + b² = c²

Where:
- **a** and **b** are the legs (the two sides that form the right angle)
- **c** is the hypotenuse (the longest side, opposite the right angle)

## Finding Different Sides:

**Finding the hypotenuse (c):**
c = √(a² + b²)

**Finding a leg (a or b):**
a = √(c² - b²)
b = √(c² - a²)

## Common Pythagorean Triples:
These are sets of three integers that satisfy the theorem:
- 3, 4, 5
- 5, 12, 13
- 8, 15, 17
- 7, 24, 25

Any multiple of these also works (e.g., 6, 8, 10 is 2 times 3, 4, 5).
      `,
      shortMethod: `
# Mr. Augustine's Pythagorean Shortcuts

**The "a² + b² = c²" Memory Trick:**
"a and b are the legs you stand on, c is the hypotenuse you slide down!"

**The "Hypotenuse is Always Biggest" Rule:**
c is ALWAYS the longest side. If you're solving for c, your answer must be bigger than both a and b!

**The "Common Triples" Shortcut:**
Memorize these and save time:
- 3-4-5 (and multiples: 6-8-10, 9-12-15, etc.)
- 5-12-13 (and multiples: 10-24-26, etc.)
- 8-15-17
- 7-24-25

If you see these numbers, don't calculate—just write the answer!

**The "Leg vs. Hypotenuse" Quick Check:**
- Finding hypotenuse? ADD the squares, then square root
- Finding a leg? SUBTRACT the squares, then square root

**The "Perfect Square" Trick:**
If a² + b² gives you a perfect square (like 25, 36, 49, 64, 100), you'll get a nice whole number!

**Calculator Shortcut:**
For a = 3, b = 4:
Type: √(3² + 4²) = √(9 + 16) = √25 = 5
      `,
      workedExamples: [
        {
          problem: 'A right triangle has legs of 6 and 8. Find the hypotenuse.',
          solution: `
**Step 1:** Identify a, b, and c
a = 6, b = 8, c = ?

**Step 2:** Apply the Pythagorean theorem
c² = a² + b²
c² = 6² + 8²
c² = 36 + 64
c² = 100

**Step 3:** Solve for c
c = √100 = 10

**Answer:** The hypotenuse is 10

**Note:** This is a 3-4-5 triple multiplied by 2!
          `
        },
        {
          problem: 'A right triangle has hypotenuse 13 and one leg 5. Find the other leg.',
          solution: `
**Step 1:** Identify what you know
c = 13, a = 5, b = ?

**Step 2:** Apply the theorem (solving for a leg)
a² + b² = c²
5² + b² = 13²
25 + b² = 169

**Step 3:** Solve for b
b² = 169 - 25
b² = 144
b = √144 = 12

**Answer:** The other leg is 12

**Note:** This is the 5-12-13 Pythagorean triple!
          `
        },
        {
          problem: 'A ladder leans against a wall. The ladder is 25 feet long and the base is 7 feet from the wall. How high up the wall does the ladder reach?',
          solution: `
**Step 1:** Draw and label
Ladder = hypotenuse = 25 ft
Base = one leg = 7 ft
Height = other leg = ?

**Step 2:** Apply Pythagorean theorem
7² + h² = 25²
49 + h² = 625

**Step 3:** Solve for h
h² = 625 - 49
h² = 576
h = √576 = 24

**Answer:** The ladder reaches 24 feet up the wall

**Note:** This is the 7-24-25 Pythagorean triple!
          `
        }
      ],
      practiceQuestions: [
        {
          id: 1,
          question: 'Right triangle has legs 3 and 4. Find the hypotenuse.',
          options: ['5', '6', '7', '8'],
          correctAnswer: 0,
          solution: 'c² = 3² + 4² = 9 + 16 = 25, so c = 5 (3-4-5 triple)'
        },
        {
          id: 2,
          question: 'Right triangle has legs 5 and 12. Find the hypotenuse.',
          options: ['11', '12', '13', '14'],
          correctAnswer: 2,
          solution: 'c² = 5² + 12² = 25 + 144 = 169, so c = 13 (5-12-13 triple)'
        },
        {
          id: 3,
          question: 'Hypotenuse is 10, one leg is 6. Find the other leg.',
          options: ['6', '7', '8', '9'],
          correctAnswer: 2,
          solution: 'b² = 10² - 6² = 100 - 36 = 64, so b = 8 (6-8-10 is 2×3-4-5)'
        },
        {
          id: 4,
          question: 'Right triangle has legs 8 and 15. Find the hypotenuse.',
          options: ['16', '17', '18', '19'],
          correctAnswer: 1,
          solution: 'c² = 8² + 15² = 64 + 225 = 289, so c = 17 (8-15-17 triple)'
        },
        {
          id: 5,
          question: 'Hypotenuse is 13, one leg is 12. Find the other leg.',
          options: ['3', '4', '5', '6'],
          correctAnswer: 2,
          solution: 'a² = 13² - 12² = 169 - 144 = 25, so a = 5 (5-12-13 triple)'
        },
        {
          id: 6,
          question: 'Right triangle has legs 9 and 12. Find the hypotenuse.',
          options: ['13', '14', '15', '16'],
          correctAnswer: 2,
          solution: 'c² = 9² + 12² = 81 + 144 = 225, so c = 15 (9-12-15 is 3×3-4-5)'
        },
        {
          id: 7,
          question: 'Hypotenuse is 25, one leg is 7. Find the other leg.',
          options: ['22', '23', '24', '25'],
          correctAnswer: 2,
          solution: 'b² = 25² - 7² = 625 - 49 = 576, so b = 24 (7-24-25 triple)'
        },
        {
          id: 8,
          question: 'Right triangle has legs 6 and 8. Find the hypotenuse.',
          options: ['8', '9', '10', '11'],
          correctAnswer: 2,
          solution: 'c² = 6² + 8² = 36 + 64 = 100, so c = 10 (6-8-10 is 2×3-4-5)'
        },
        {
          id: 9,
          question: 'Hypotenuse is 17, one leg is 8. Find the other leg.',
          options: ['13', '14', '15', '16'],
          correctAnswer: 2,
          solution: 'b² = 17² - 8² = 289 - 64 = 225, so b = 15 (8-15-17 triple)'
        },
        {
          id: 10,
          question: 'Right triangle has legs 5 and 12. What is c²?',
          options: ['119', '144', '169', '194'],
          correctAnswer: 2,
          solution: 'c² = 5² + 12² = 25 + 144 = 169'
        },
        {
          id: 11,
          question: 'Hypotenuse is 20, one leg is 12. Find the other leg.',
          options: ['14', '15', '16', '17'],
          correctAnswer: 2,
          solution: 'b² = 20² - 12² = 400 - 144 = 256, so b = 16 (12-16-20 is 4×3-4-5)'
        },
        {
          id: 12,
          question: 'Right triangle has legs 7 and 24. Find the hypotenuse.',
          options: ['23', '24', '25', '26'],
          correctAnswer: 2,
          solution: 'c² = 7² + 24² = 49 + 576 = 625, so c = 25 (7-24-25 triple)'
        },
        {
          id: 13,
          question: 'Hypotenuse is 15, one leg is 9. Find the other leg.',
          options: ['10', '11', '12', '13'],
          correctAnswer: 2,
          solution: 'b² = 15² - 9² = 225 - 81 = 144, so b = 12 (9-12-15 is 3×3-4-5)'
        },
        {
          id: 14,
          question: 'Right triangle has legs 10 and 24. Find the hypotenuse.',
          options: ['24', '25', '26', '27'],
          correctAnswer: 2,
          solution: 'c² = 10² + 24² = 100 + 576 = 676, so c = 26 (10-24-26 is 2×5-12-13)'
        },
        {
          id: 15,
          question: 'Hypotenuse is 26, one leg is 10. Find the other leg.',
          options: ['22', '23', '24', '25'],
          correctAnswer: 2,
          solution: 'b² = 26² - 10² = 676 - 100 = 576, so b = 24 (10-24-26 is 2×5-12-13)'
        },
        {
          id: 16,
          question: 'Right triangle has legs 15 and 20. Find the hypotenuse.',
          options: ['23', '24', '25', '26'],
          correctAnswer: 2,
          solution: 'c² = 15² + 20² = 225 + 400 = 625, so c = 25 (15-20-25 is 5×3-4-5)'
        },
        {
          id: 17,
          question: 'Hypotenuse is 34, one leg is 16. Find the other leg.',
          options: ['28', '29', '30', '31'],
          correctAnswer: 2,
          solution: 'b² = 34² - 16² = 1156 - 256 = 900, so b = 30 (16-30-34 is 2×8-15-17)'
        },
        {
          id: 18,
          question: 'Right triangle has legs 20 and 21. Find the hypotenuse.',
          options: ['27', '28', '29', '30'],
          correctAnswer: 2,
          solution: 'c² = 20² + 21² = 400 + 441 = 841, so c = 29 (20-21-29 triple)'
        },
        {
          id: 19,
          question: 'Hypotenuse is 29, one leg is 20. Find the other leg.',
          options: ['19', '20', '21', '22'],
          correctAnswer: 2,
          solution: 'b² = 29² - 20² = 841 - 400 = 441, so b = 21 (20-21-29 triple)'
        },
        {
          id: 20,
          question: 'Right triangle has legs 9 and 40. Find the hypotenuse.',
          options: ['39', '40', '41', '42'],
          correctAnswer: 2,
          solution: 'c² = 9² + 40² = 81 + 1600 = 1681, so c = 41 (9-40-41 triple)'
        }
      ]
    },
    {
      id: 'pythagorean-applications',
      title: 'Pythagorean Theorem Applications',
      description: 'Apply the theorem to real-world problems and distance',
      textbookExplanation: `
# Applications of the Pythagorean Theorem

The Pythagorean Theorem is incredibly useful for solving real-world problems involving right triangles.

## Common Applications:

**1. Distance Problems:**
Finding the straight-line distance between two points when you know horizontal and vertical distances.

**2. Ladder Problems:**
Finding how high a ladder reaches on a wall, or how far the base must be from the wall.

**3. Diagonal Problems:**
Finding the diagonal of a rectangle, square, or other shapes.

**4. Navigation Problems:**
Finding the shortest distance when traveling in perpendicular directions.

## Distance Formula (Coordinate Plane):
The distance between points (x₁, y₁) and (x₂, y₂) is:
d = √[(x₂ - x₁)² + (y₂ - y₁)²]

This is just the Pythagorean Theorem applied to coordinates!

## 3D Pythagorean Theorem:
For a rectangular box with dimensions a, b, and c, the space diagonal d is:
d = √(a² + b² + c²)

## Problem-Solving Strategy:
1. Draw a diagram
2. Identify the right triangle
3. Label the sides (legs and hypotenuse)
4. Apply a² + b² = c²
5. Solve for the unknown
      `,
      shortMethod: `
# Mr. Augustine's Application Shortcuts

**The "Draw It First" Rule:**
ALWAYS sketch the problem. Right triangles hide everywhere in word problems!

**The "Diagonal = Pythagorean" Trick:**
Any time you see "diagonal," think Pythagorean Theorem!
- Rectangle diagonal: d = √(length² + width²)
- Square diagonal: d = side√2

**The "Distance Formula is Pythagorean" Insight:**
Distance between two points? It's just a² + b² = c² in disguise!
- Horizontal distance = |x₂ - x₁|
- Vertical distance = |y₂ - y₁|
- Straight distance = √(horizontal² + vertical²)

**The "Ladder Problem Template":**
- Ladder length = hypotenuse (always!)
- Distance from wall = one leg
- Height on wall = other leg

**The "3D Box Diagonal" Formula:**
Space diagonal = √(length² + width² + height²)
It's like Pythagorean Theorem with an extra dimension!

**The "Check Your Answer" Rule:**
- Hypotenuse must be the longest side
- All sides must be positive
- If you get a weird answer, check your setup!

**Real-World Tip:**
Round to a reasonable precision (usually 1-2 decimal places for real measurements).
      `,
      workedExamples: [
        {
          problem: 'A baseball diamond is a square with sides of 90 feet. How far does the catcher throw from home plate to second base?',
          solution: `
**Step 1:** Draw the diagram
The throw from home to second base is the diagonal of the square.

**Step 2:** Identify the right triangle
The diagonal splits the square into two right triangles.
Legs = 90 ft each
Hypotenuse = diagonal = ?

**Step 3:** Apply Pythagorean theorem
d² = 90² + 90²
d² = 8100 + 8100
d² = 16,200

**Step 4:** Solve
d = √16,200 ≈ 127.3 feet

**Answer:** The catcher throws approximately 127.3 feet
          `
        },
        {
          problem: 'Find the distance between points (2, 3) and (8, 11) on a coordinate plane.',
          solution: `
**Step 1:** Find horizontal and vertical distances
Horizontal: |8 - 2| = 6
Vertical: |11 - 3| = 8

**Step 2:** Apply distance formula (Pythagorean theorem)
d = √(6² + 8²)
d = √(36 + 64)
d = √100
d = 10

**Answer:** The distance is 10 units

**Note:** This is a 6-8-10 triangle (2 times 3-4-5)!
          `
        },
        {
          problem: 'A rectangular box is 12 inches long, 5 inches wide, and 4 inches tall. Find the space diagonal.',
          solution: `
**Step 1:** Identify the 3D Pythagorean formula
d = √(length² + width² + height²)

**Step 2:** Substitute values
d = √(12² + 5² + 4²)
d = √(144 + 25 + 16)
d = √185

**Step 3:** Calculate
d ≈ 13.6 inches

**Answer:** The space diagonal is approximately 13.6 inches
          `
        }
      ],
      practiceQuestions: [
        {
          id: 1,
          question: 'A rectangle is 6 cm by 8 cm. Find the diagonal.',
          options: ['9 cm', '10 cm', '11 cm', '12 cm'],
          correctAnswer: 1,
          solution: 'd = √(6² + 8²) = √(36 + 64) = √100 = 10 cm (6-8-10 triple)'
        },
        {
          id: 2,
          question: 'Distance between (0, 0) and (3, 4)?',
          options: ['4', '5', '6', '7'],
          correctAnswer: 1,
          solution: 'd = √(3² + 4²) = √(9 + 16) = √25 = 5 (3-4-5 triple)'
        },
        {
          id: 3,
          question: 'A square has side 10. Find the diagonal.',
          options: ['10√2', '10√3', '20', '10'],
          correctAnswer: 0,
          solution: 'd = √(10² + 10²) = √200 = 10√2 ≈ 14.14'
        },
        {
          id: 4,
          question: 'A 13-foot ladder leans on a wall. Base is 5 feet from wall. How high does it reach?',
          options: ['10 ft', '11 ft', '12 ft', '13 ft'],
          correctAnswer: 2,
          solution: 'h = √(13² - 5²) = √(169 - 25) = √144 = 12 ft (5-12-13 triple)'
        },
        {
          id: 5,
          question: 'Distance between (1, 2) and (4, 6)?',
          options: ['4', '5', '6', '7'],
          correctAnswer: 1,
          solution: 'd = √[(4-1)² + (6-2)²] = √(9 + 16) = √25 = 5'
        },
        {
          id: 6,
          question: 'A TV screen is 24 inches wide and 18 inches tall. Find the diagonal.',
          options: ['28 in', '29 in', '30 in', '31 in'],
          correctAnswer: 2,
          solution: 'd = √(24² + 18²) = √(576 + 324) = √900 = 30 in'
        },
        {
          id: 7,
          question: 'A box is 6×8×10 inches. Find the space diagonal.',
          options: ['12 in', '13 in', '14 in', '15 in'],
          correctAnswer: 2,
          solution: 'd = √(6² + 8² + 10²) = √(36 + 64 + 100) = √200 ≈ 14.14 in'
        },
        {
          id: 8,
          question: 'Distance between (-2, 3) and (3, 15)?',
          options: ['11', '12', '13', '14'],
          correctAnswer: 2,
          solution: 'd = √[(3-(-2))² + (15-3)²] = √(25 + 144) = √169 = 13'
        },
        {
          id: 9,
          question: 'A rectangle is 15 cm by 20 cm. Find the diagonal.',
          options: ['23 cm', '24 cm', '25 cm', '26 cm'],
          correctAnswer: 2,
          solution: 'd = √(15² + 20²) = √(225 + 400) = √625 = 25 cm (15-20-25 is 5×3-4-5)'
        },
        {
          id: 10,
          question: 'A person walks 12 blocks east and 5 blocks north. How far from start?',
          options: ['11 blocks', '12 blocks', '13 blocks', '14 blocks'],
          correctAnswer: 2,
          solution: 'd = √(12² + 5²) = √(144 + 25) = √169 = 13 blocks (5-12-13 triple)'
        },
        {
          id: 11,
          question: 'Distance between (0, 0) and (8, 15)?',
          options: ['15', '16', '17', '18'],
          correctAnswer: 2,
          solution: 'd = √(8² + 15²) = √(64 + 225) = √289 = 17 (8-15-17 triple)'
        },
        {
          id: 12,
          question: 'A square has diagonal 20. Find the side length.',
          options: ['10', '10√2', '14.14', '20/√2'],
          correctAnswer: 1,
          solution: 'If d = s√2, then s = d/√2 = 20/√2 = 10√2 ≈ 14.14'
        },
        {
          id: 13,
          question: 'A 25-foot ladder reaches 24 feet up a wall. How far is base from wall?',
          options: ['5 ft', '6 ft', '7 ft', '8 ft'],
          correctAnswer: 2,
          solution: 'b = √(25² - 24²) = √(625 - 576) = √49 = 7 ft (7-24-25 triple)'
        },
        {
          id: 14,
          question: 'Distance between (2, 5) and (10, 11)?',
          options: ['8', '9', '10', '11'],
          correctAnswer: 2,
          solution: 'd = √[(10-2)² + (11-5)²] = √(64 + 36) = √100 = 10'
        },
        {
          id: 15,
          question: 'A rectangle is 9 m by 12 m. Find the diagonal.',
          options: ['13 m', '14 m', '15 m', '16 m'],
          correctAnswer: 2,
          solution: 'd = √(9² + 12²) = √(81 + 144) = √225 = 15 m (9-12-15 is 3×3-4-5)'
        },
        {
          id: 16,
          question: 'A box is 3×4×12 feet. Find the space diagonal.',
          options: ['12 ft', '13 ft', '14 ft', '15 ft'],
          correctAnswer: 1,
          solution: 'd = √(3² + 4² + 12²) = √(9 + 16 + 144) = √169 = 13 ft'
        },
        {
          id: 17,
          question: 'Distance between (-3, -4) and (0, 0)?',
          options: ['4', '5', '6', '7'],
          correctAnswer: 1,
          solution: 'd = √[(-3)² + (-4)²] = √(9 + 16) = √25 = 5 (3-4-5 triple)'
        },
        {
          id: 18,
          question: 'A kite string is 100 m long. Kite is 60 m high. How far horizontally?',
          options: ['70 m', '75 m', '80 m', '85 m'],
          correctAnswer: 2,
          solution: 'h = √(100² - 60²) = √(10000 - 3600) = √6400 = 80 m'
        },
        {
          id: 19,
          question: 'Distance between (5, 1) and (8, 5)?',
          options: ['4', '5', '6', '7'],
          correctAnswer: 1,
          solution: 'd = √[(8-5)² + (5-1)²] = √(9 + 16) = √25 = 5'
        },
        {
          id: 20,
          question: 'A rectangle is 7 cm by 24 cm. Find the diagonal.',
          options: ['23 cm', '24 cm', '25 cm', '26 cm'],
          correctAnswer: 2,
          solution: 'd = √(7² + 24²) = √(49 + 576) = √625 = 25 cm (7-24-25 triple)'
        }
      ]
    }
  ]
};